/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import EmailTemplateEditor from './EmailTemplateEditor';
import BusinessOfficeEmailDeliveries from './BusinessOfficeEmailDeliveries';

const CUSTOMER_TYPES = ['Internal', 'External'];

const TYPE_BLURB = {
  Internal: 'Sent for payers who are NC State users. Their account numbers are charged directly by the business office.',
  External: "Sent for payers who are not NC State users, or whose department is Commercial Client. The business office invoices them directly, so this template carries the customer's mailing address from the invoice's Bill To block instead of an NC State account to charge."
};

/**
 * Admin editor for the business-office billing request emails.
 *
 * One billing request goes out per payer once every designated payer has signed
 * an invoice. Everything the business office sees - subject, body, recipients,
 * and whether the invoice PDF rides along - is configured here.
 */
const EmailServiceManagement = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeType, setActiveType] = useState('Internal');
  const [drafts, setDrafts] = useState({});
  const [variables, setVariables] = useState([]);
  const [serviceEnabled, setServiceEnabled] = useState(true);
  const [togglingService, setTogglingService] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get('/api/business-office-email');
      const byType = {};
      for (const setting of res.data.settings || []) {
        byType[setting.customer_type] = {
          ...setting,
          to_addresses: (setting.to_addresses || []).join(', '),
          cc_addresses: (setting.cc_addresses || []).join(', ')
        };
      }
      setDrafts(byType);
      setServiceEnabled(res.data.service_enabled !== false);
      setVariables(res.data.variables || []);
    } catch (error) {
      console.error('Error loading email settings:', error);
      toast.error('Failed to load email settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const draft = drafts[activeType];

  const updateDraft = (field, value) => {
    setDrafts(prev => ({
      ...prev,
      [activeType]: { ...prev[activeType], [field]: value }
    }));
  };

  // The master switch saves on its own rather than waiting for "Save Changes",
  // so pausing the service is never left half-applied.
  const handleToggleService = async (enabled) => {
    setTogglingService(true);
    try {
      await axios.put('/api/business-office-email/service', { enabled });
      setServiceEnabled(enabled);
      toast.success(enabled ? 'Email service turned on' : 'Email service turned off');
    } catch (error) {
      console.error('Error toggling email service:', error);
      toast.error(error.response?.data?.message || 'Failed to update the email service');
    } finally {
      setTogglingService(false);
    }
  };

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const res = await axios.put(`/api/business-office-email/${activeType}`, {
        subject_template: draft.subject_template,
        body_template: draft.body_template,
        to_addresses: draft.to_addresses,
        cc_addresses: draft.cc_addresses,
        cc_include_pi: draft.cc_include_pi,
        cc_include_billing_contact: draft.cc_include_billing_contact,
        cc_include_billed_by: draft.cc_include_billed_by,
        attach_invoice_pdf: draft.attach_invoice_pdf,
        enabled: draft.enabled
      });
      setDrafts(prev => ({
        ...prev,
        [activeType]: {
          ...res.data,
          to_addresses: (res.data.to_addresses || []).join(', '),
          cc_addresses: (res.data.cc_addresses || []).join(', ')
        }
      }));
      toast.success(`${activeType} email template saved`);
    } catch (error) {
      console.error('Error saving email settings:', error);
      toast.error(error.response?.data?.message || 'Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  const handleResetTemplate = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`/api/business-office-email/${activeType}/reset-template`);
      setDrafts(prev => ({
        ...prev,
        [activeType]: {
          ...prev[activeType],
          subject_template: res.data.subject_template,
          body_template: res.data.body_template
        }
      }));
      toast.success('Template restored to the default');
    } catch (error) {
      console.error('Error resetting template:', error);
      toast.error('Failed to reset template');
    } finally {
      setSaving(false);
    }
  };

  // A live template with nowhere to send to is rejected on save.
  const missingToAddress =
    serviceEnabled && !!draft?.enabled && !(draft?.to_addresses || '').trim();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        Email Service
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Business office billing requests. When every designated payer has confirmed their accounts
        and signed an invoice, one billing request is emailed per payer using the template for that
        payer&apos;s customer type.
      </Typography>

      {/* Master switch */}
      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          mb: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          borderColor: serviceEnabled ? 'success.light' : 'warning.light',
          bgcolor: serviceEnabled ? 'transparent' : 'warning.50'
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Email service {serviceEnabled ? 'on' : 'off'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {serviceEnabled
              ? 'Billing requests are emailed to the business office once every payer has signed an invoice.'
              : 'No billing requests are emailed when an invoice is signed. Invoices still complete normally and you can send the details to the business office yourself.'}
          </Typography>
        </Box>
        <Switch
          checked={serviceEnabled}
          disabled={togglingService}
          onChange={(e) => handleToggleService(e.target.checked)}
        />
      </Paper>

      <BusinessOfficeEmailDeliveries />

      {/* A branded tab strip: white text on the theme colour throughout, with the
          underline marking which one is selected. `on-primary` rather than a
          literal white, so a light theme colour still gets readable dark text. */}
      <Tabs
        value={activeType}
        onChange={(e, value) => setActiveType(value)}
        sx={{
          mb: 2.5,
          borderRadius: 1,
          overflow: 'hidden',
          backgroundColor: 'var(--brand-primary, #c41e3a)',
          minHeight: 44,
          '& .MuiTab-root': {
            color: 'var(--brand-on-primary, #fff)',
            opacity: 0.75,
            fontWeight: 500,
            textTransform: 'none',
            minHeight: 44
          },
          '& .MuiTab-root.Mui-selected': {
            color: 'var(--brand-on-primary, #fff)',
            opacity: 1,
            fontWeight: 700
          },
          '& .MuiTabs-indicator': {
            height: 3,
            backgroundColor: 'var(--brand-on-primary, #fff)'
          }
        }}
      >
        {CUSTOMER_TYPES.map(type => (
          <Tab key={type} value={type} label={`${type} Customers`} />
        ))}
      </Tabs>

      {!draft ? (
        <Alert severity="error">No settings found for {activeType} customers.</Alert>
      ) : (
        <>
          <Alert severity="info" sx={{ mb: 2.5 }}>
            {TYPE_BLURB[activeType]}
          </Alert>

          <Paper variant="outlined" sx={{ px: 2.5, py: 1.5, mb: 2.5 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!draft.enabled}
                  onChange={(e) => updateDraft('enabled', e.target.checked)}
                />
              }
              label={draft.enabled
                ? `Sending enabled for ${activeType} customers`
                : `Sending paused for ${activeType} customers`}
            />
            {draft.enabled && !serviceEnabled && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
                Nothing will go out while the email service is off.
              </Typography>
            )}
          </Paper>

          {/* Recipients */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
              Recipients
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="To"
              value={draft.to_addresses || ''}
              onChange={(e) => updateDraft('to_addresses', e.target.value)}
              placeholder="business-office@ncsu.edu"
              required={missingToAddress}
              error={missingToAddress}
              helperText={missingToAddress
                ? 'Required while the email service is on and this template is enabled.'
                : 'Business office inbox. Separate multiple addresses with commas.'}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              size="small"
              label="CC (additional fixed addresses)"
              value={draft.cc_addresses || ''}
              onChange={(e) => updateDraft('cc_addresses', e.target.value)}
              placeholder="optional@ncsu.edu"
              helperText="Separate multiple addresses with commas. Added on top of the automatic CCs below."
              sx={{ mb: 1 }}
            />
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Automatically CC per invoice
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!draft.cc_include_pi}
                    onChange={(e) => updateDraft('cc_include_pi', e.target.checked)}
                  />
                }
                label="The project's PI"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!draft.cc_include_billing_contact}
                    onChange={(e) => updateDraft('cc_include_billing_contact', e.target.checked)}
                  />
                }
                label="The payer's billing person"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!draft.cc_include_billed_by}
                    onChange={(e) => updateDraft('cc_include_billed_by', e.target.checked)}
                  />
                }
                label="The admin who billed the invoice"
              />
            </Box>
          </Paper>

          {/* Attachments */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Attachments
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={!!draft.attach_invoice_pdf}
                  onChange={(e) => updateDraft('attach_invoice_pdf', e.target.checked)}
                />
              }
              label="Attach the signed invoice PDF"
            />
          </Paper>

          {/* Template */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Email Template
              </Typography>
              <Button size="small" onClick={handleResetTemplate} disabled={saving}>
                Restore Default
              </Button>
            </Box>

            <EmailTemplateEditor
              subject={draft.subject_template || ''}
              body={draft.body_template || ''}
              variables={variables}
              onSubjectChange={(value) => updateDraft('subject_template', value)}
              onBodyChange={(value) => updateDraft('body_template', value)}
            />
          </Paper>

          {/* Save */}
          <Paper variant="outlined" sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              {missingToAddress
                ? 'Add a "To" address before saving, or pause this template.'
                : `Changes apply to the ${activeType} customer template only.`}
            </Typography>
            <Button variant="outlined" onClick={fetchSettings} disabled={saving}>
              Discard Changes
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || missingToAddress}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default EmailServiceManagement;
